import { Component, computed, effect, inject, input, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Chart, TimeScaleOptions, TimeUnit, Tooltip } from "chart.js";
import "chartjs-adapter-luxon";
import { DateTime, DurationLike } from "luxon";

import { ButtonComponent } from "../../components/button/button.component";

import { FormsModule } from "@angular/forms";
import { RadioButtonModule } from "@openng/optimus-ui/radiobutton";
import { PopoverModule } from "@openng/optimus-ui/popover";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { NgClass } from "@angular/common";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { GateFault } from "../../../models/api/fault/gateFault";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { first } from "rxjs";
import { SelectModule } from "@openng/optimus-ui/select";
import { DatePickerModule } from "@openng/optimus-ui/datepicker";
import { PeriodQuickSelection, PERIOD_QUICK_SELECTIONS } from "../../../constants/periodQuickSelections";
import { GroupingLevel } from "../../../constants/groupingLevel";
@Component({
  standalone: true,
  selector: "app-gate-fault-frequency",
  imports: [
    TranslateModule,
    PopoverModule,
    ButtonComponent,
    FormsModule,
    SelectModule,
    DatePickerModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    NgClass,
    TimeoutComponent,
    ProgressSpinnerModule,
  ],
  templateUrl: "./gate-fault-frequency.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./gate-fault-frequency.component.scss",
})
export class GateFaultFrequencyComponent {
  public chartId = input<string>();
  public gateFaultSource = input<GateFault[] | null>();
  public aspectRatio = input<number>();

  public loading = computed(() => this.gateFaultSource() === null);
  public timeOut = createTimeoutSignal(this.loading);

  private chart!: Chart;
  private translate = inject(TranslateService);

  // Variables for new filter options:

  public selectedStartDate: Date = DateTime.now().startOf("day").toJSDate();
  public selectedEndDate: Date = DateTime.now().endOf("day").toJSDate();

  private startDate!: DateTime;
  private endDate!: DateTime;

  public readonly quickSelectionOptions = [...PERIOD_QUICK_SELECTIONS];
  public selectedQuickSelection: PeriodQuickSelection = "today";

  public buttonText: string = this.translate.instant("gate_fault_frequency_widget.period");
  public periodOption: "date" | "quick-selection" = "date";

  constructor() {
    Chart.register([Tooltip]);
    effect(() => {
      const gateFaults = this.gateFaultSource();
      const chartId = this.chartId();
      if (gateFaults && chartId) {
        this.initChart(chartId);
        const timeStart = DateTime.now().startOf("day").toMillis();
        const timeEnd = DateTime.now().endOf("day").toMillis();
        this.createChartData(gateFaults, timeStart, timeEnd, "hour");
      }
    });

    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe({
      next: () => {
        this.setLabels();
        this.updateTimeseriesLocale(this.chart, "x", this.translate.currentLang);
      },
    });
  }

  /**
   * Save the selected time period. Depending on the selected periodOption,
   * it either uses the selected dates or the selected quick selection.
   */
  public saveTimeSelection() {
    switch (this.periodOption) {
      case "date":
        this.saveTimeSelectionViaDate();
        break;
      case "quick-selection":
        this.saveTimeSelectionViaQuickSelection();
        break;
    }
  }

  /**
   * Updates the selected date range and generates chart data
   * based on this range. Sets the x-axis unit and grouping
   * level according to the selected dates.
   */
  private saveTimeSelectionViaDate() {
    this.startDate = DateTime.fromJSDate(this.selectedStartDate).startOf("day");
    this.endDate = DateTime.fromJSDate(this.selectedEndDate).endOf("day");
    if (this.startDate && this.endDate) {
      this.setXUnitViaDate();
      const groupingLevel = this.getGroupingLevelDate();
      const start = this.startDate.toMillis();
      const end = this.endDate.toMillis();
      const gateFaults = this.gateFaultSource();
      if (gateFaults) {
        this.createChartData(gateFaults, start, end, groupingLevel);
      }
    }
  }

  /**
   * Sets the start and end dates based on the selected quick selection.
   * The selected dates are used to generate chart data and set the x-axis unit and grouping level.
   * @param selectedQuickSelection - The selected quick selection.
   */
  private saveTimeSelectionViaQuickSelection() {
    switch (this.selectedQuickSelection) {
      case "today":
        this.startDate = DateTime.now().startOf("day");
        this.endDate = DateTime.now().endOf("day");
        break;
      case "yesterday":
        this.startDate = DateTime.now().minus({ days: 1 }).startOf("day");
        this.endDate = DateTime.now().minus({ days: 1 }).endOf("day");
        break;
      case "day_before_yesterday":
        this.startDate = DateTime.now().minus({ days: 2 }).startOf("day");
        this.endDate = DateTime.now().minus({ days: 2 }).endOf("day");
        break;
      case "this_week":
        this.startDate = DateTime.now().startOf("week");
        this.endDate = DateTime.now().endOf("week");
        break;
      case "last_week":
        this.startDate = DateTime.now().minus({ weeks: 1 }).startOf("week");
        this.endDate = DateTime.now().minus({ weeks: 1 }).endOf("week");
        break;
      case "this_month":
        this.startDate = DateTime.now().startOf("month");
        this.endDate = DateTime.now().endOf("month");
        break;
      case "last_month":
        this.startDate = DateTime.now().minus({ months: 1 }).startOf("month");
        this.endDate = DateTime.now().minus({ months: 1 }).endOf("month");
        break;
      case "this_year":
        this.startDate = DateTime.now().startOf("year");
        this.endDate = DateTime.now().endOf("year");
        break;
      case "last_year":
        this.startDate = DateTime.now().minus({ years: 1 }).startOf("year");
        this.endDate = DateTime.now().minus({ years: 1 }).endOf("year");
        break;
    }
    this.setXUnitViaQuickSelection();
    const gateFaults = this.gateFaultSource();
    if (gateFaults) {
      this.createChartData(
        gateFaults,
        this.startDate.toMillis(),
        this.endDate.toMillis(),
        this.getGroupingLevelQuickSelection(this.selectedQuickSelection)
      );
    }
  }

  /**
   * Determines the grouping level based on a quick selection filter value.
   *
   * @param filterValue - The quick selection filter value indicating the desired time period.
   * @returns The corresponding DateTimeUnit ("hour", "day", or "month") for the provided filter value.
   * Defaults to "hour" for unrecognized filter values.
   */
  private getGroupingLevelQuickSelection(filterValue: PeriodQuickSelection): GroupingLevel {
    switch (filterValue) {
      case "today":
      case "yesterday":
      case "day_before_yesterday":
        return "hour";
      case "this_week":
      case "last_week":
      case "this_month":
      case "last_month":
        return "day";
      case "this_year":
      case "last_year":
        return "month";
    }
  }

  /**
   * Calculates the grouping level for the given start and end date.
   *
   * @returns The grouping level ("hour", "day", or "month") based on the duration between the start and end date.
   * If the duration is 3 days or less, the grouping level is "hour".
   * If the duration is 45 days or less, the grouping level is "day".
   * Otherwise, the grouping level is "month".
   */
  private getGroupingLevelDate() {
    const start = this.startDate.toMillis();
    const end = this.endDate.toMillis();
    const diff = end - start;

    // 3 Days
    if (diff <= 259200000) {
      return "hour";
    } else if (diff <= 3888000000) {
      return "day";
    } else {
      return "month";
    }
  }
  /**
   * Updates the unit of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param unit The unit to use for the axis.
   */
  private updateTimeseriesUnit(chart: Chart, axis: "x" | "y", unit: TimeUnit) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.time.unit = unit;
    }
  }
  /**
   * Updates the x-axis unit of the chart based on the duration between the start and end date.
   */
  private setXUnitViaDate() {
    const start = this.startDate.toMillis();
    const end = this.endDate.toMillis();
    const diff = end - start;

    if (diff <= 86400000) {
      this.updateTimeseriesUnit(this.chart, "x", "hour");
    } else if (diff <= 3888000000) {
      this.updateTimeseriesUnit(this.chart, "x", "day");
    } else {
      this.updateTimeseriesUnit(this.chart, "x", "month");
    }
  }

  /**
   * Updates the x-axis unit of the chart based on the selected quick selection.
   */
  private setXUnitViaQuickSelection() {
    switch (this.selectedQuickSelection) {
      case "today":
      case "yesterday":
      case "day_before_yesterday":
        this.updateTimeseriesUnit(this.chart, "x", "hour");
        break;
      case "this_week":
      case "last_week":
      case "this_month":
      case "last_month":
        this.updateTimeseriesUnit(this.chart, "x", "day");
        break;
      case "this_year":
      case "last_year":
        this.updateTimeseriesUnit(this.chart, "x", "month");
        break;
    }
  }

  /**
   * Creates the chart data based on the gate faults. The data is grouped by the given unit.
   * @param sourceRes - The gate faults to create the chart data from.
   * @param timeStart - The start time of the data range.
   * @param timeEnd - The end time of the data range.
   * @param group - The grouping level for the data (e.g. hour, day, month).
   */
  private createChartData(sourceRes: GateFault[], timeStart: number, timeEnd: number, group: GroupingLevel) {
    const criticalData: { x: number; y: number }[] = [];
    const majorData: { x: number; y: number }[] = [];
    // const yesterday = DateTime.now().minus({ days: 1 }).toMillis();
    // Only faults from last 24 hours
    sourceRes
      .filter((fault) => fault.triggeredAt && fault.triggeredAt >= timeStart && fault.triggeredAt <= timeEnd)
      .forEach((fault) => {
        if (fault.faultType === "CRITICAL" && fault.triggeredAt) {
          criticalData.push({
            x: fault.triggeredAt,
            y: 1,
          });
        } else if (fault.faultType === "MAJOR" && fault.triggeredAt) {
          majorData.push({
            x: fault.triggeredAt,
            y: 1,
          });
        }
      });
    const groupedCritical = criticalData.reduce((acc: Record<string, number>, point: { x: number; y: number }) => {
      // Round down to the start of the hour
      const timeFrame = DateTime.fromMillis(point.x).startOf(group).toISO();
      if (timeFrame) {
        if (!acc[timeFrame]) {
          acc[timeFrame] = 0;
        }

        acc[timeFrame] += point.y;
      }

      return acc;
    }, {});

    const aggregatedCriticalData = Object.keys(groupedCritical).map((hour) => ({
      x: DateTime.fromISO(hour).toMillis(),
      y: groupedCritical[hour],
    }));

    const groupedMajor = majorData.reduce((acc: Record<string, number>, point: { x: number; y: number }) => {
      // Round down to the start of the hour
      const timeFrame = DateTime.fromMillis(point.x).startOf(group).toISO();
      if (timeFrame) {
        if (!acc[timeFrame]) {
          acc[timeFrame] = 0;
        }

        acc[timeFrame] += point.y;
      }

      return acc;
    }, {});
    const aggregatedMajorData = Object.keys(groupedMajor).map((hour) => ({
      x: DateTime.fromISO(hour).toMillis(),
      y: groupedMajor[hour],
    }));

    const startingPoint = DateTime.fromMillis(timeStart);
    const endPoint = DateTime.fromMillis(timeEnd);
    this.chart.data.datasets[0].data = aggregatedMajorData;
    this.chart.data.datasets[1].data = aggregatedCriticalData;
    let currentTime = startingPoint;
    while (currentTime <= endPoint) {
      const timeFrame: string | null = currentTime.toISO();
      if (timeFrame !== null && !groupedMajor[timeFrame]) {
        this.chart.data.datasets[0].data.push({
          x: currentTime.toMillis(),
          y: 0,
        });
      }
      if (timeFrame !== null && !groupedCritical[timeFrame]) {
        this.chart.data.datasets[1].data.push({
          x: currentTime.toMillis(),
          y: 0,
        });
      }
      currentTime = currentTime.plus(this.getGroupingLevelCalculation(group));
    }

    this.chart.update();
  }

  /**
   * Returns the duration between each point of the given view level.
   *
   * @param viewLevel The view level to calculate the duration for.
   * @returns The duration between each point of the given view level.
   */
  private getGroupingLevelCalculation(viewLevel: GroupingLevel): DurationLike {
    switch (viewLevel) {
      case "hour":
        return { hours: 1 };
      case "day":
        return { days: 1 };
      case "month":
        return { months: 1 };
    }
  }

  /**
   * Initializes the bar chart.
   * Called when the component is initialized, or when the chart data changes.
   */
  private initChart(chartId: string) {
    if (this.chart) {
      this.chart.destroy();
    }
    this.chart = new Chart(chartId, {
      type: "bar",
      data: {
        datasets: [
          {
            label: "",
            data: [],
            backgroundColor: "#EAB62F28",
            borderColor: "#EAB62F",
          },
          {
            label: "",
            data: [],
            backgroundColor: "#ff634728",
            borderColor: "#ff6347",
          },
        ],
      },
      options: {
        interaction: {
          mode: "x",
        },
        aspectRatio: this.aspectRatio(),
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          bar: {
            borderWidth: 2,
            hoverBorderWidth: 50,
          },
        },
        scales: {
          y: {
            stacked: true,
            title: {
              display: true,
              text: "",
            },
            border: {
              display: false,
            },
            grid: {
              color: "#727272",
            },
            ticks: {
              color: "#ffffff",
              precision: 0,
            },
          },
          x: {
            stacked: true,
            type: "timeseries",
            adapters: {
              date: {
                locale: this.translate.currentLang,
              },
            },
            time: {
              unit: "hour",
              displayFormats: {
                hour: "HH:mm",
                day: "dd.MM",
              },
            },
            title: {
              display: true,
              text: "",
            },
            grid: {
              display: false,
              color: "#727272",
            },
            ticks: {
              color: "#ffffff",
            },
          },
        },
      },
    });
    this.setLabels();
  }

  /**
   * Updates the title of the given axis on the given chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param title The title to use for the axis.
   */
  private updateScaleLabel(chart: Chart, axis: "x" | "y", title: string) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.title.text = title;
    }
  }

  /**
   * Updates the locale of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param locale The locale to use for the axis.
   */
  private updateTimeseriesLocale(chart: Chart, axis: "x" | "y", locale: string) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.adapters = {
        date: { locale },
      };
      this.chart.update();
    }
  }

  /**
   * Translates and updates the labels for the bar chart datasets and axes.
   * Retrieves translations for critical and major fault labels, and axis titles
   * for faults and time. Updates the chart with these translated labels and titles.
   */
  private setLabels() {
    this.translate
      .get(["critical", "major", "gate_fault_frequency_widget.faults", "common.time"])
      .pipe(first())
      .subscribe({
        next: (translations) => {
          if (this.chart) {
            this.chart.data.datasets[0].label = translations["major"];
            this.chart.data.datasets[1].label = translations["critical"];
            this.updateScaleLabel(this.chart, "y", translations["gate_fault_frequency_widget.faults"]);
            this.updateScaleLabel(this.chart, "x", translations["common.time"]);
            this.chart.update();
          }
        },
      });
  }
}
